import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

import {
  ArrowLeft,
  BookOpen,
  BrainCircuit,
  BarChart3,
  FileText,
  CheckCircle,
  MonitorSmartphone,
  Users,
  Target,
  Zap,
  ChevronRight,
  GraduationCap,
  BookMarked,
  Sparkles,
  ClipboardList,
  TrendingUp,
  ShieldCheck,
} from 'lucide-react';

import { Button } from '@/components/ui/button';

const DLPPage = () => {
  const features = [
    {
      icon: BookOpen,
      title: 'Smart Notes',
      description:
        'Chapter-wise notes, concise explanations, revision material, and formula sheets.',
    },
    {
      icon: ClipboardList,
      title: 'Practice Questions',
      description:
        'Topic-wise MCQs, numerical problems, PYQs, and difficulty-based practice.',
    },
    {
      icon: BarChart3,
      title: 'Performance Analytics',
      description:
        'Track weak topics, accuracy, speed, and overall academic progress.',
    },
    {
      icon: BrainCircuit,
      title: 'Smart Learning Tools',
      description:
        'Smart doubt solving, explanations, study plans, and personalized recommendations.',
    },
    {
      icon: Users,
      title: 'Mentorship',
      description:
        'Guidance, accountability, study strategies, and structured preparation support.',
    },
    {
      icon: MonitorSmartphone,
      title: 'Learn Anywhere',
      description:
        'Access your learning portal from mobile, tablet, or desktop devices.',
    },
  ];

  const whyDLP = [
    'Students use too many disconnected resources',
    'Learning often becomes confusing and unstructured',
    'Students struggle to identify weak areas',
    'Traditional studying lacks performance analytics',
    'Modern education needs smarter digital systems',
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Digital Learning Portal - Physics Notebook</title>
        <meta
          name="description"
          content="Physics Notebook Digital Learning Portal (DLP) — Smart learning platform for Physics students with notes, tests, analytics, AI tools, and mentorship."
        />
      </Helmet>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Button
          variant="ghost"
          asChild
          className="text-gray-600 hover:bg-gray-100"
        >
          <Link to="/" className="flex items-center gap-2 group">
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            <span>Back to Homepage</span>
          </Link>
        </Button>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-600 to-blue-600 py-20 md:py-28 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4 mr-2" />
                Physics Notebook Platform
              </div>

              <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
                Digital Learning Portal
              </h1>

              <p className="text-xl md:text-2xl text-blue-100 leading-relaxed max-w-4xl mx-auto mb-10">
                Study Physics smarter with structured notes, practice systems,
                analytics, digital tools, mentorship, and personalized learning —
                all in one digital platform.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-10">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
                  <div className="text-3xl font-bold">AI</div>
                  <div className="text-blue-100">Powered Learning</div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
                  <div className="text-3xl font-bold">24/7</div>
                  <div className="text-blue-100">Digital Access</div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
                  <div className="text-3xl font-bold">Smart</div>
                  <div className="text-blue-100">Performance Tracking</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="bg-white text-indigo-700 hover:bg-indigo-50 px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all">
                  <a href="https://dlp.physicsnotebook.in" target="_blank" rel="noopener noreferrer">
                    Explore DLP
                  </a>
                </Button>

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is DLP */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <GraduationCap className="w-10 h-10 text-indigo-600" />
            </div>

            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What is DLP?
            </h2>

            <p className="text-xl text-gray-600 leading-relaxed">
              Physics Notebook DLP (Digital Learning Portal) is a centralized
              online learning ecosystem designed to simplify Physics education
              using modern technology, structured content, intelligent analytics,
              and AI-powered tools.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Core Features
            </h2>

            <p className="text-xl text-gray-600">
              Everything students need in one platform
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-xl transition-all"
                >
                  <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-indigo-600" />
                  </div>

                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                    {feature.title}
                  </h3>

                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why DLP Exists */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why We Built DLP
              </h2>

              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Many students struggle not because they lack potential,
                but because learning systems are fragmented, confusing,
                and difficult to track.
              </p>

              <ul className="space-y-4">
                {whyDLP.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <ChevronRight className="w-5 h-5 text-indigo-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-indigo-600 to-blue-600 rounded-3xl p-10 text-white"
            >
              <Target className="w-14 h-14 mb-6" />

              <h3 className="text-3xl font-bold mb-6">
                Our Goal
              </h3>

              <p className="text-lg text-blue-100 leading-relaxed mb-6">
                To create a modern digital ecosystem where students can:
              </p>

              <div className="space-y-4">
                {[
                  'Study efficiently',
                  'Track performance',
                  'Practice strategically',
                  'Learn conceptually',
                  'Improve consistently',
                ].map((item, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-3 text-green-300" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
            >
              <ShieldCheck className="w-12 h-12 text-indigo-300 mb-6" />

              <h3 className="text-3xl font-bold mb-4">
                Our Vision
              </h3>

              <p className="text-gray-300 text-lg leading-relaxed">
                To make quality Physics education accessible, structured,
                technology-driven, and genuinely helpful for every student.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
            >
              <Zap className="w-12 h-12 text-yellow-300 mb-6" />

              <h3 className="text-3xl font-bold mb-4">
                Our Mission
              </h3>

              <p className="text-gray-300 text-lg leading-relaxed">
                To combine education, analytics, AI, and modern digital tools
                into a platform that improves how students study and understand
                Physics.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <TrendingUp className="w-16 h-16 mx-auto mb-6" />

            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Start Learning Smarter
            </h2>

            <p className="text-xl text-indigo-100 leading-relaxed mb-10">
              Join Physics Notebook DLP and experience a smarter,
              organized, and technology-driven way to study Physics.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-white text-indigo-700 hover:bg-indigo-50 px-8 py-6 text-lg font-medium shadow-lg">
                <a href="https://dlp.physicsnotebook.in" target="_blank" rel="noopener noreferrer">
                    Get Started
                  </a>
              </Button>
                <Link to="/contact">
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-indigo-700 px-8 py-6 text-lg font-medium"
              >
                Contact Us
              </Button></Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DLPPage;